import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const AUDIENCES = [
  {
    id: "consumers",
    index: "01",
    kicker: "The individual",
    title: "For Consumers",
    line: "Clarity and access at every financial decision.",
    tone: "light",
    items: [
      "Discover trusted financial products.",
      "Compare providers easily.",
      "Receive AI-powered recommendations.",
      "Make informed financial decisions.",
      "Access verified financial institutions.",
      "Enjoy a seamless digital experience.",
    ],
  },
  {
    id: "institutions",
    index: "02",
    kicker: "The institution",
    title: "For Financial Institutions",
    line: "Qualified growth with less risk and stronger recovery.",
    tone: "light",
    items: [
      "Generate qualified leads.",
      "Reduce customer acquisition costs.",
      "Verify borrowers instantly.",
      "Reduce fraud.",
      "Improve lending decisions.",
      "Increase recovery rates.",
      "Automate operations.",
      "Strengthen customer relationships.",
      "Grow sustainably.",
    ],
  },
  {
    id: "economy",
    index: "03",
    kicker: "The continent",
    title: "For Africa's Economy",
    line: "Inclusion, trust, and responsible growth at scale.",
    tone: "dark",
    items: [
      "Promote financial inclusion.",
      "Increase digital trust.",
      "Support responsible lending.",
      "Accelerate financial innovation.",
      "Strengthen financial institutions.",
      "Improve access to finance.",
      "Drive economic growth.",
    ],
  },
];

function Benefits() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="benefits"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="benefits-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_45%_at_0%_0%,rgba(229,184,11,0.1),transparent_55%),radial-gradient(50%_40%_at_100%_8%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 pb-0 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Benefits of SimFinance
          </p>
          <h2
            id="benefits-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Designed for everyone in
            <span className="mt-1 block">the financial ecosystem</span>
          </h2>
        </motion.header>

        <nav
          className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-y border-navy/10 py-5"
          aria-label="Benefit audiences"
        >
          {AUDIENCES.map((audience, index) => (
            <a
              key={audience.id}
              href={`#${audience.id}`}
              className="group inline-flex items-baseline gap-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              <span className="font-mono text-[11px] text-gold">{audience.index}</span>
              <span className="font-semibold text-navy/70 group-hover:text-navy">
                {audience.title.replace("For ", "")}
              </span>
              {index < AUDIENCES.length - 1 ? (
                <span className="hidden text-navy/20 sm:inline" aria-hidden="true">
                  /
                </span>
              ) : null}
            </a>
          ))}
        </nav>

        <div className="mt-4">
          {AUDIENCES.filter((audience) => audience.tone !== "dark").map((audience, index, list) => (
            <AudienceChapter
              key={audience.id}
              audience={audience}
              reduce={reduce}
              fade={fade}
              compactBottom={index === list.length - 1}
            />
          ))}
        </div>
      </div>

      <AudienceChapter
        audience={AUDIENCES[2]}
        reduce={reduce}
        fade={fade}
        compactTop
      />
    </section>
  );
}

function AudienceChapter({
  audience,
  reduce,
  fade,
  bleed = false,
  compactBottom = false,
  compactTop = false,
}) {
  const dark = audience.tone === "dark";
  const count = String(audience.items.length).padStart(2, "0");

  return (
    <motion.article
      id={audience.id}
      className={`relative scroll-mt-16 ${
        dark
          ? "overflow-hidden border-t border-white/10 bg-navy text-white"
          : "border-t border-navy/10 text-navy"
      }`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      {dark ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
          <div className="pointer-events-none absolute -right-20 top-8 h-56 w-56 rounded-full bg-royal/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 left-1/4 h-40 w-40 rounded-full bg-gold/10 blur-3xl" />
        </>
      ) : null}

      <div
        className={`relative grid gap-10 lg:grid-cols-[minmax(16rem,0.42fr)_minmax(0,1fr)] lg:gap-16 ${
          compactBottom ? "pb-8 pt-14 lg:pb-10 lg:pt-16" : compactTop ? "pb-20 pt-10 lg:pb-28 lg:pt-12" : "py-14 lg:py-16"
        } ${bleed || compactTop ? "mx-auto max-w-8xl px-4 sm:px-6 lg:px-8" : ""}`}
      >
        <div className="relative">
          <div className="flex items-center gap-3">
            <span
              className={`font-mono text-xs font-semibold tracking-[0.22em] ${
                dark ? "text-gold" : "text-navy/35"
              }`}
            >
              {audience.index} / 03
            </span>
            <span className={`h-px w-8 ${dark ? "bg-gold/50" : "bg-navy/15"}`} />
            <span
              className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${
                dark ? "text-white/40" : "text-navy/35"
              }`}
            >
              {audience.kicker}
            </span>
          </div>
          <h3
            className={`mt-5 text-3xl font-semibold tracking-tight sm:text-[2.05rem] ${
              dark ? "" : "text-royal"
            }`}
          >
            {audience.title}
          </h3>
          <p className={`mt-3 max-w-sm text-[15px] leading-relaxed ${dark ? "text-white/60" : "text-navy/55"}`}>
            {audience.line}
          </p>
          <p
            className={`mt-6 font-mono text-[11px] tracking-[0.18em] ${
              dark ? "text-white/35" : "text-navy/35"
            }`}
          >
            {count} benefits
          </p>
        </div>

        <ul className="relative grid gap-x-8 sm:grid-cols-2">
          {audience.items.map((item, i) => (
            <motion.li
              key={item}
              className={`flex items-start gap-3 border-t py-3.5 ${
                dark ? "border-white/10" : "border-navy/10"
              }`}
              initial={reduce ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={
                reduce ? { duration: 0 } : { duration: 0.4, delay: 0.04 * i, ease: EASE }
              }
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                  dark ? "bg-gold text-navy" : "bg-navy text-white"
                }`}
              >
                <CheckIcon />
              </span>
              <span className={`text-[15px] leading-snug ${dark ? "text-white/90" : "text-navy/75"}`}>
                <span
                  className={`mr-2 font-mono text-[10px] tracking-[0.14em] ${
                    dark ? "text-white/35" : "text-navy/30"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                {item}
              </span>
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3 w-3">
      <path
        d="M2.4 6.2L4.8 8.6L9.6 3.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Benefits;
